import React, { useEffect, useState, useCallback } from 'react';
import { X, AlertTriangle, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../services/api';

const IncidentModal = () => {
  const [incident, setIncident] = useState(null);
  const [lastCheckedId, setLastCheckedId] = useState(null);

  const checkActiveIncident = useCallback(async () => {
    try {
      const response = await api.get('/incident-modal/active');
      if (response.data) {
        const sessionKey = `incident_dismissed_${response.data.id}`;
        const wasDismissed = sessionStorage.getItem(sessionKey);
        
        // Show modal if it's a new incident or not previously dismissed
        if (!wasDismissed && response.data.id !== lastCheckedId) {
          setIncident(response.data);
          setLastCheckedId(response.data.id);
        } else if (!wasDismissed && !incident) {
          // Re-show if incident exists but modal was somehow closed
          setIncident(response.data);
        }
      } else {
        // No active incident
        setIncident(null);
      }
    } catch (error) {
      // Only log non-404 errors
      if (error.response?.status !== 404) {
        console.error('Error fetching incident:', error);
      }
    }
  }, [lastCheckedId, incident]);

  useEffect(() => {
    // Initial check immediately on mount
    checkActiveIncident();
    
    // Set up polling every 30 seconds for faster detection
    const interval = setInterval(checkActiveIncident, 30000);
    
    // Also check when user returns to the tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkActiveIncident();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkActiveIncident]);

  const handleDismiss = () => {
    if (incident) {
      sessionStorage.setItem(`incident_dismissed_${incident.id}`, 'true');
    }
    setIncident(null);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
        return 'border-[#FF3B5C] bg-[#FF3B5C]/10';
      case 'high':
        return 'border-[#FFB800] bg-[#FFB800]/10';
      default:
        return 'border-[#00FFFF] bg-[#00FFFF]/10';
    }
  };

  const getPriorityIcon = (priority) => {
    if (priority === 'critical' || priority === 'high') {
      return <AlertTriangle className="h-6 w-6 text-[#FFB800]" />;
    }
    return <Info className="h-6 w-6 text-[#00FFFF]" />;
  };

  return (
    <AnimatePresence>
      {incident && (
        <>
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={handleDismiss} />
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 50 }}
            transition={{ duration: 0.3 }}
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-2xl px-4"
            data-testid="incident-modal"
          >
            <div className={`bg-[#1A1A1D] rounded-lg border-2 ${getPriorityColor(incident.priority)} shadow-2xl`}>
              <div className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 mt-1">
                    {getPriorityIcon(incident.priority)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-2xl font-bold text-white mb-3">{incident.title}</h3>
                    <p className="text-[#E8E8E8] text-lg leading-relaxed">{incident.message}</p>
                  </div>
                  <button
                    onClick={handleDismiss}
                    className="flex-shrink-0 p-2 hover:bg-[#222226] rounded-lg transition-colors"
                    data-testid="close-incident-modal"
                  >
                    <X className="h-6 w-6 text-[#B8B8BA] hover:text-white" />
                  </button>
                </div>
              </div>
              <div className="bg-[#222226] px-6 py-4 rounded-b-lg flex items-center justify-between">
                <span className="text-[#6B6B6E] text-sm">
                  {new Date(incident.created_at).toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
                <button
                  onClick={handleDismiss}
                  className="btn-primary py-2 px-6"
                >
                  Compris
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default IncidentModal;